import { Controller } from 'stimulus'
import Sortable from 'sortablejs'

export default class extends Controller {
  static targets = ['favoritesSection', 'modulesSection']

  connect() {
    this.favorites = JSON.parse(this.data.get('favorites') || '[]')
    this.counts    = {}
    this.sortables = []

    this.interceptClick = this.interceptClick.bind(this)
    this.element.addEventListener('click', this.interceptClick, true)

    this.loadCounts()
  }

  disconnect() {
    this.element.removeEventListener('click', this.interceptClick, true)
    this.destroySortables()
  }

  get editing() {
    return this.element.dataset.mode === 'edit'
  }

  customize() {
    this.element.dataset.mode = 'edit'
    this.buildSortables()
  }

  finish() {
    this.element.dataset.mode = 'daily'
    this.destroySortables()
  }

  // Captured before rails-ujs, so a link with data-method cannot fire while customizing.
  interceptClick(event) {
    if (!this.editing) return

    const link = event.target.closest('a')
    if (!link || !this.element.contains(link)) return

    event.preventDefault()
    event.stopPropagation()
    if (link.dataset.pillKey) this.toggleKey(link.dataset.pillKey)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.toggleKey(event.currentTarget.dataset.key)
  }

  toggleKey(key) {
    const favorites = this.favorites.includes(key)
      ? this.favorites.filter(favorite => favorite !== key)
      : this.favorites.concat(key)

    this.save(favorites).then(() => this.redraw())
  }

  refused(response) {
    return response.json().catch(() => ({})).then(body => {
      if (body.error && window.flashAlert) window.flashAlert(body.error)
    })
  }

  saveOrder() {
    const tiles = this.favoritesSectionTarget.querySelectorAll('[data-tile-home-sortable] > [data-key]')
    this.save(Array.from(tiles).map(tile => tile.dataset.key))
  }

  save(favorites) {
    this.favorites = favorites
    const token = document.querySelector('meta[name="csrf-token"]')

    return fetch(this.data.get('preferencesUrl'), {
      method:      'PATCH',
      credentials: 'same-origin',
      headers:     {
        'Content-Type': 'application/json',
        Accept:         'application/json',
        'X-CSRF-Token': token ? token.content : ''
      },
      body: JSON.stringify({ favorite_tiles: favorites })
    }).then(response => (response.ok ? response : this.refused(response)))
  }

  redraw() {
    return fetch(window.location.href, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(response => response.text())
      .then(html => {
        const page = new DOMParser().parseFromString(html, 'text/html')
        const root = page.querySelector('[data-tile-home-favorites]')
        if (root) this.favorites = JSON.parse(root.dataset.tileHomeFavorites || '[]')
        this.replace(page, 'favoritesSection')
        this.replace(page, 'modulesSection')
        this.paintCounts()
        this.loadCounts()
        if (this.editing) this.buildSortables()
      })
  }

  replace(page, name) {
    const fresh = page.querySelector(`[data-target="tile-home.${name}"]`)
    if (fresh) this[`${name}Target`].innerHTML = fresh.innerHTML
  }

  buildSortables() {
    this.destroySortables()
    this.favoritesSectionTarget.querySelectorAll('[data-tile-home-sortable]').forEach(list => {
      this.sortables.push(Sortable.create(list, {
        animation:       150,
        filter:          'button',
        preventOnFilter: false,
        onEnd:           () => this.saveOrder()
      }))
    })
  }

  destroySortables() {
    this.sortables.forEach(sortable => sortable.destroy())
    this.sortables = []
  }

  loadCounts() {
    fetch(this.data.get('countsUrl'), { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(response => (response.ok ? response.json() : {}))
      .then(counts => {
        this.counts = counts
        this.paintCounts()
      })
      .catch(() => {})
  }

  paintCounts() {
    Object.keys(this.counts).forEach(key => this.showCount(key, this.counts[key]))
  }

  showCount(key, { count, hint, tone }) {
    const danger = tone === 'danger'

    this.element.querySelectorAll(`[data-tile-count="${key}"]`).forEach(node => {
      node.textContent = count
      node.classList.remove('text-slate-300')
      node.classList.add('text-slate-900')
    })

    if (hint) {
      this.element.querySelectorAll(`[data-tile-hint="${key}"]`).forEach(node => {
        node.textContent = hint
        node.classList.toggle('text-rose-600', danger)
        node.classList.toggle('font-medium', danger)
        node.classList.toggle('text-slate-400', !danger)
      })
    }

    this.element.querySelectorAll(`[data-tile-badge="${key}"]`).forEach(node => {
      if (!count) return

      node.textContent = count
      node.style.display = ''
      if (danger) node.classList.replace('bg-slate-100', 'bg-rose-50')
      if (danger) node.classList.replace('text-slate-600', 'text-rose-700')
    })
  }
}
